import React, { useEffect, useRef, useState } from 'react';
import { Loader, Dimmer, TableBody, TableCell, TableRow } from 'semantic-ui-react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import { Mensaje, MensajeSiNo } from '../mensajes';
import moment from 'moment';
import useTableScroll from '../useTableScroll';
import { imprimirReportePicking } from './reportePickingPDF';
import { imprimirPedido } from './reportePedidoPDF';
import './modalReportePicking.css';

const TIPO_PEDIDO = '0';
const TIPO_NOTA = '1';
const STATUS_POR_DESPACHAR = '0';
const STATUS_DESPACHADO = '1';

const fechaValida = (valor) => /^\d{4}-\d{2}-\d{2}$/.test(String(valor || ''));

const letraUnidad = (unidad) => {
  const nombre = String(unidad || '').trim().toUpperCase();
  if (nombre.indexOf('BULT') >= 0) return 'B';
  if (nombre.indexOf('CAJ') >= 0) return 'C';
  if (nombre.indexOf('PAQ') >= 0) return 'P';
  if (nombre.indexOf('DOCEN') >= 0) return 'D';
  return nombre === '' ? 'U' : nombre.charAt(0);
};

/**
 * Agrupa el detalle de /api/pedido/notasPicking en documentos (pedidos o
 * notas). La funcion de base de datos devuelve una fila por renglon e incluye
 * los datos del encabezado, por lo que no se consultan las tablas desde el
 * cliente.
 */
const agruparDocumentos = (filas, esNota) => {
  const documentos = {};

  filas.forEach((fila) => {
    const id = esNota ? fila.idnotaentrega : fila.idpedido;
    const numero = esNota ? fila.numnotaentrega : fila.numpedido;

    if (id === undefined || id === null) return;

    if (!documentos[id]) {
      documentos[id] = {
        id: id,
        numero: numero,
        cliente: fila.cliente || '',
        idvendedor: fila.idvendedor !== undefined ? fila.idvendedor : null,
        idalmacen: fila.idalmacen !== undefined ? fila.idalmacen : null,
        rif: fila.rif || fila.cedularif || '',
        fecha: fila.fecha || null,
        totalmonto: fila.totalmonto !== undefined ? fila.totalmonto : null,
        filas: [],
      };
    }

    documentos[id].filas.push(fila);
  });

  return Object.keys(documentos)
    .map((id) => documentos[id])
    .sort((a, b) => Number(a.id) - Number(b.id));
};

/**
 * Convierte las notas de entrega seleccionadas al formato que espera
 * reportePickingPDF. La existencia real se consulta por almacen porque el
 * detalle de la nota no la incluye.
 *
 * El detalle de la nota guarda cantidad + tipounidad (0 = unidad principal,
 * 1 = unidad alterna), igual que fn_reporte_picking.
 */
const construirPickingNotas = (documentos, existencias, unidades, productosInfo) => {
  const productos = {};
  const almacenesPorProducto = {};
  const infoProductos = productosInfo || {};

  documentos.forEach((documento) => {
    documento.filas.forEach((fila) => {
      const idproducto = fila.idproducto;
      const info = infoProductos[idproducto] || {};
      const cantidad = Number(fila.cantidad) || 0;

      if (!productos[idproducto]) {
        productos[idproducto] = {
          idproducto: idproducto,
          codproducto: fila.codproducto || '',
          nomproducto: fila.nomproducto || info.nombre || '',
          cantidadPrincipal: 0,
          cantidadUnidades: 0,
          factor: Number(info.factor) || 0,
          unidadPrincipal: unidades[fila.idunidadmedida] || '',
          unidadAlterna: unidades[fila.idunidadalterna] || '',
          usaPrincipal: false,
          usaAlterna: false,
          observaciones: [],
        };
      }

      const producto = productos[idproducto];

      // tipounidad = 0: unidad principal (BULTO/CAJA); 1: unidad alterna
      if (Number(fila.tipounidad) === 0) {
        producto.cantidadPrincipal += cantidad;
        producto.usaPrincipal = true;
      } else {
        producto.cantidadUnidades += cantidad;
        producto.usaAlterna = true;
      }

      if (!producto.nomproducto && info.nombre) producto.nomproducto = info.nombre;
      if (!producto.factor && Number(info.factor) > 0) producto.factor = Number(info.factor);
      if (!producto.unidadPrincipal) producto.unidadPrincipal = unidades[fila.idunidadmedida] || '';
      if (!producto.unidadAlterna) producto.unidadAlterna = unidades[fila.idunidadalterna] || '';

      if (fila.observacion && producto.observaciones.indexOf(fila.observacion) < 0) {
        producto.observaciones.push(fila.observacion);
      }

      if (documento.idalmacen !== null && documento.idalmacen !== undefined) {
        if (!almacenesPorProducto[idproducto]) almacenesPorProducto[idproducto] = [];
        if (almacenesPorProducto[idproducto].indexOf(documento.idalmacen) < 0) {
          almacenesPorProducto[idproducto].push(documento.idalmacen);
        }
      }
    });
  });

  return Object.keys(productos)
    .map((id) => productos[id])
    .sort((a, b) => {
      const codigoA = String(a.codproducto || '');
      const codigoB = String(b.codproducto || '');
      return codigoA < codigoB ? -1 : codigoA > codigoB ? 1 : 0;
    })
    .map((producto, indice) => {
      const almacenes = almacenesPorProducto[producto.idproducto] || [];
      let existenciaTotal = 0;

      almacenes.forEach((idalmacen) => {
        const existencia = existencias[idalmacen + '|' + producto.idproducto];
        if (!existencia) return;

        existenciaTotal += Number(existencia.cantidad) || 0;

        if (!producto.factor && Number(existencia.factor) > 0) {
          producto.factor = Number(existencia.factor);
        }
        if (!producto.unidadPrincipal && existencia.descripcionuni) {
          producto.unidadPrincipal = existencia.descripcionuni;
        }
        if (!producto.nomproducto && existencia.nombre) {
          producto.nomproducto = existencia.nombre;
        }
      });

      const letra = letraUnidad(producto.unidadPrincipal);
      const unidad = producto.usaPrincipal || !producto.usaAlterna
        ? producto.unidadPrincipal
        : producto.unidadAlterna;
      const pedidaPrincipal = Math.trunc(producto.cantidadPrincipal);
      const pedidaUnidades = Math.trunc(producto.cantidadUnidades);
      let cantidadTotal = '0';

      if (pedidaPrincipal > 0 && pedidaUnidades > 0) {
        cantidadTotal = pedidaPrincipal + letra + '-' + pedidaUnidades + 'U';
      } else if (pedidaPrincipal > 0) {
        cantidadTotal = pedidaPrincipal + letra;
      } else if (pedidaUnidades > 0) {
        cantidadTotal = pedidaUnidades + 'U';
      }

      // sin factor (producto sin unidad principal) la existencia va en unidades
      const existenciaPrincipal = producto.factor > 0
        ? Math.floor(existenciaTotal / producto.factor)
        : null;
      const existenciaUnidades = producto.factor > 0
        ? existenciaTotal - existenciaPrincipal * producto.factor
        : null;

      return {
        orden_picking: indice + 1,
        codproducto: producto.codproducto,
        nomproducto: producto.nomproducto,
        cantidad_total: cantidadTotal,
        cantidad_existencia: existenciaTotal,
        cantidad_principal: existenciaPrincipal,
        cantidad_unidades: existenciaUnidades,
        unidadprincipal: letra,
        unidad: unidad,
        tipounidad: 0,
        observacion: producto.observaciones.join(' / '),
      };
    });
};


const mensajeError = (error) => {
  if (error && error.response && error.response.data && error.response.data.message) {
    return error.response.data.message;
  }
  return error && error.message ? error.message : 'Error al consultar los datos';
};

export const ModalPicking = (props) => {

  var op = require('../../../public/modulos/datos');
  let token = sessionStorage.getItem('jwtToken');
  const fechasistema = JSON.parse(sessionStorage.getItem('fechasistema'));
  const afiliado = JSON.parse(sessionStorage.getItem("riforganizacion"));
  const usuario = JSON.parse(sessionStorage.getItem("login"));

  const fechaInicial = fechasistema && moment(fechasistema).isValid()
    ? moment(fechasistema).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD');

  const btnCancela = useRef();
  const btnAcepta = useRef();
  const btnSalir = useRef();
  const consultaId = useRef(0);

  const [desde, setDesde] = useState(fechaInicial);
  const [hasta, setHasta] = useState(fechaInicial);
  const [tipo, setTipo] = useState(TIPO_PEDIDO);
  const [statusdespacho, setStatusdespacho] = useState(STATUS_POR_DESPACHAR);
  const [idvendedor, setIdvendedor] = useState(0);
  const [vendedores, setVendedores] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [activate, setActivate] = useState(false);
  const [mensaje, setMensaje] = useState({ mostrar: false, titulo: '', texto: '', icono: '' });
  const [mensajesino, setMensajesino] = useState({ mostrar: false, titulo: '', texto: '', icono: '' });
  const [records, setRecords] = useState([]);
  const [idseleccionados, setIdseleccionados] = useState([]);

  const esNota = Number(tipo) === 1;
  const todosSeleccionados = records.length > 0 && idseleccionados.length === records.length;

  const unidadPorId = {};
  unidades.forEach((unidad) => {
    unidadPorId[unidad.idunidadmedida] = String(unidad.descripcion || '').trim();
  });

  const nombreVendedor = (id) => {
    if (id === null || id === undefined || id === '') return '';
    const vendedor = vendedores.find((v) => String(v.idagente) === String(id));
    return vendedor ? vendedor.nombre : '';
  };

  const handleCheckTodos = (e) => {
    if (e.target.checked) {
      setIdseleccionados(records.map((registro) => registro.id));
    } else {
      setIdseleccionados([]);
    }
  };

  const handleCheckDocumento = (id) => (e) => {
    if (e.target.checked) {
      setIdseleccionados((seleccionados) => (
        seleccionados.indexOf(id) >= 0 ? seleccionados : seleccionados.concat([id])
      ));
    } else {
      setIdseleccionados((seleccionados) => seleccionados.filter((actual) => actual !== id));
    }
  };

  const headCells = [
    {
      label: (
        <div
          className="form-check d-flex justify-content-center align-items-center m-0"
          style={{ paddingLeft: 0 }}
        >
          <input
            className="form-check-input"
            type="checkbox"
            title="Seleccionar todos"
            checked={todosSeleccionados}
            onChange={handleCheckTodos}
            style={{ position: 'relative', margin: 0, float: 'none' }}
          />
        </div>
      ),
      width: "50px",
      textAlign: "center",
    },
    { label: esNota ? 'N° NOTA' : 'N° PEDIDO', width: '100px', textAlign: 'center' },
    { label: 'CLIENTE', textAlign: 'left' },
    { label: 'VENDEDOR', textAlign: 'left' },
    { label: 'FECHA', width: '90px', textAlign: 'center' },
    { label: 'RENGLONES', width: '80px', textAlign: 'center' },
    { label: 'VER', width: '45px', textAlign: 'center' },
  ];

  // La tabla es corta (300px): el encabezado no debe quedar fijo mientras se
  // revisan las filas seleccionadas ni superponerse con el scroll horizontal.
  const { TblContainer, TblHead } = useTableScroll(headCells, { stickyHeader: false });

  /**
   * Deja el modal como recien abierto: limpia la seleccion, el listado y los
   * mensajes, y devuelve los filtros a sus valores por defecto para que al
   * volver a abrirlo se muestren los documentos en su estado actual.
   */
  const blanquear = () => {
    consultaId.current++;
    setActivate(false);
    setIdseleccionados([]);
    setRecords([]);
    setDesde(fechaInicial);
    setHasta(fechaInicial);
    setTipo(TIPO_PEDIDO);
    setStatusdespacho(STATUS_POR_DESPACHAR);
    setIdvendedor(0);
    setMensaje({ mostrar: false, titulo: '', texto: '', icono: '' });
    setMensajesino({ mostrar: false, titulo: '', texto: '', icono: '' });
  };

  const cancelar = () => {
    blanquear();
    props.onHideCancelar();
  };

  const cerrarVentana = (ventana) => {
    if (ventana && !ventana.closed) ventana.close();
  };

  /**
   * Marca como despachados los documentos seleccionados que se acaban de
   * imprimir con el boton Imprimir. Se ejecuta solamente despues de que el PDF
   * se genero correctamente.
   */
  const marcarDespachados = (idsDocumentos, esNotaDocumento) => {
    // En la vista "DESPACHADOS" los documentos ya estan despachados: no se
    // vuelve a actualizar su estado para que sigan en la lista al reimprimir.
    if (Number(statusdespacho) === 1) return Promise.resolve(0);

    const ids = Array.from(new Set(
      (Array.isArray(idsDocumentos) ? idsDocumentos : [])
        .map((id) => String(id).trim())
        .filter((id) => /^\d+$/.test(id))
    ));

    if (ids.length === 0) return Promise.resolve(0);

    return axios
      .post(
        `${op.conexion}/api/pedido/actualizarstatusdespacho`,
        {
          por: esNotaDocumento ? 1 : 0,
          ids: ids,
        },
        {
          headers: {
            "x-access-token": `${token}`,
          },
        }
      )
      .then(function (response) {
        const idsActualizados = new Set(
          ((response.data && response.data.ids) || []).map((id) => String(id))
        );

        if (idsActualizados.size > 0) {
          setRecords((registros) => registros.filter(
            (registro) => !idsActualizados.has(String(registro.id))
          ));
          setIdseleccionados((seleccionados) => seleccionados.filter(
            (id) => !idsActualizados.has(String(id))
          ));
        }

        return (response.data && response.data.actualizados) || 0;
      })
      .catch(function (error) {
        setMensaje({
          mostrar: true,
          titulo: 'Error',
          texto: 'El PDF se generó, pero no se pudo actualizar el estado de despacho: ' + mensajeError(error),
          icono: 'error',
        });
        return 0;
      });
  };

  const cambiarDesde = (valor) => {
    setDesde(valor);
    if (valor && hasta && valor > hasta) setHasta(valor);
  };

  const cambiarHasta = (valor) => {
    setHasta(valor);
    if (valor && desde && valor < desde) setDesde(valor);
  };

  const selectVendedores = () => {
    axios
      .get(`${op.conexion}/api/consulta/modelirows`, {
        params: {
          campos: 'idagente, nombre',
          id: true,
          nomtab: 'agente',
          nomid: 'vendedor',
        },
        headers: {
          "x-access-token": `${token}`,
        },
      })
      .then(function (response) {
        if (response.status === 200 && Array.isArray(response.data)) {
          const lista = response.data
            .slice()
            .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));
          setVendedores(lista);
        }
      })
      .catch(function (error) {
        setMensaje({
          mostrar: true,
          titulo: 'Error',
          texto: mensajeError(error),
          icono: 'error',
        });
      });
  };

  const selectUnidades = () => {
    axios
      .get(`${op.conexion}/api/consulta/selecionarregistrocondi`, {
        params: {
          campos: 'idunidadmedida, descripcion',
          nomtab: 'unidadmedida',
          condi: '1=1',
          orden: 'idunidadmedida',
        },
        headers: {
          "x-access-token": `${token}`,
        },
      })
      .then(function (response) {
        if (response.status === 200 && Array.isArray(response.data)) {
          setUnidades(response.data);
        }
      })
      .catch(function () {
        // el nombre de la unidad solo es informativo en el PDF
      });
  };

  /**
   * La funcion de base de datos es la unica fuente de datos: recibe el
   * vendedor, el tipo de documento y el rango de fechas, y devuelve tanto los
   * datos del encabezado como el detalle de cada renglon.
   */
  const cargarDocumentos = (fechaDesde, fechaHasta, tipoActual, vendedorActual, statusActual) => {
    const idConsulta = ++consultaId.current;

    if (!fechaValida(fechaDesde) || !fechaValida(fechaHasta)) {
      setRecords([]);
      setIdseleccionados([]);
      setActivate(false);
      return;
    }

    const esNotaActual = Number(tipoActual) === 1;

    setActivate(true);

    axios
      .get(`${op.conexion}/api/pedido/notasPicking`, {
        params: {
          por: tipoActual,
          status: Number(statusActual) === 1 ? 1 : 0,
          esvendedor: Number(vendedorActual) || 0,
          desde: fechaDesde,
          hasta: fechaHasta,
        },
        headers: {
          "x-access-token": `${token}`,
        },
      })
      .then(function (response) {
        if (idConsulta !== consultaId.current) return;

        const filas = Array.isArray(response.data) ? response.data : [];
        setRecords(agruparDocumentos(filas, esNotaActual));
        setIdseleccionados([]);
      })
      .catch(function (error) {
        if (idConsulta !== consultaId.current) return;
        setRecords([]);
        setIdseleccionados([]);
        setMensaje({
          mostrar: true,
          titulo: 'Error',
          texto: mensajeError(error),
          icono: 'error',
        });
      })
      .finally(() => {
        if (idConsulta === consultaId.current) setActivate(false);
      });
  };

  useEffect(() => {
    if (!props.show) return;
    if (vendedores.length === 0) selectVendedores();
    if (unidades.length === 0) selectUnidades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show]);

  useEffect(() => {
    if (!props.show) return;
    cargarDocumentos(desde, hasta, tipo, idvendedor, statusdespacho);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show, desde, hasta, tipo, idvendedor, statusdespacho]);


  /**
   * Muestra / imprime un documento individual (pedido o nota de entrega).
   * Esta accion es solo de consulta: NO modifica el estado de despacho. El
   * estado se actualiza unicamente al imprimir los documentos seleccionados
   * con el boton Imprimir.
   */
  const imprimirPedidoSeleccionado = (lineas, numero, ventana, tipoDocumento) => {

    setActivate(true);

    imprimirPedido(
      lineas,
      {
        empresa: afiliado,
        usuario: usuario,
        numpedido: numero,
        tipoDocumento: tipoDocumento || 'PEDIDO',
      },
      ventana
    )
      .catch(function (error) {
        cerrarVentana(ventana);
        setMensaje({
          mostrar: true,
          titulo: "Error",
          texto: error.message,
          icono: "error",
        });
      })
      .finally(() => {
        setActivate(false);
      });
  };

  const totalDocumento = (item) => (
    item.totalmonto !== null && item.totalmonto !== undefined
      ? Number(item.totalmonto)
      : item.filas.reduce((acumulado, fila) => {
        const cantidad = Number(fila.cantidad) || 0;
        const precio = Number(fila.punitario) || 0;
        const iva = Number(fila.iva) || 0;
        return acumulado + cantidad * precio * (1 + iva / 100);
      }, 0)
  );

  const lineasDocumento = (item, campoNumero) => item.filas.map((fila) => {
    const precio = Number(fila.punitario) || 0;
    const iva = Number(fila.iva) || 0;

    return {
      [campoNumero]: item.numero,
      cliente: item.cliente,
      rif: item.rif,
      totalmonto: totalDocumento(item),
      codproducto: fila.codproducto,
      nomproducto: fila.nomproducto || '',
      cantidad: fila.cantidad,
      punitario: fila.punitario,
      ivaproducto: precio * iva / 100,
      tipounidad: fila.tipounidad,
      unidaprincipal: unidadPorId[fila.idunidadmedida] || fila.unidad || '',
      unidadalterna: unidadPorId[fila.idunidadalterna] || '',
    };
  });

  /**
   * El detalle y los datos del documento ya vienen de la funcion, por lo que
   * la impresion no vuelve a consultar pedido/nota ni producto.
   */
  const selectPedidos = (item) => {
    if (!item.filas || item.filas.length === 0) {
      setMensaje({ mostrar: true, titulo: 'Notificación', texto: '¡ No se encontraron renglones para el pedido !', icono: 'informacion' });
      return;
    }

    const ventana = window.open('', 'PedidoPicking', 'fullscreen=no,scrollbars=yes,toolbar=no,resizable=no,width=850,height=700');
    const lineas = lineasDocumento(item, 'numpedido');

    imprimirPedidoSeleccionado(lineas, item.numero, ventana, 'PEDIDO');
  };

  const verNotaEntrega = (item) => {
    if (!item.filas || item.filas.length === 0) {
      setMensaje({ mostrar: true, titulo: 'Notificación', texto: '¡ No se encontraron renglones para la nota de entrega !', icono: 'informacion' });
      return;
    }

    const ventana = window.open('', 'NotaEntregaPicking', 'fullscreen=no,scrollbars=yes,toolbar=no,resizable=no,width=850,height=700');
    const lineas = lineasDocumento(item, 'numnotaentrega');

    imprimirPedidoSeleccionado(lineas, item.numero, ventana, 'NOTA DE ENTREGA');
  };

  const selectDocumento = (item) => {
    if (esNota) {
      verNotaEntrega(item);
    } else {
      selectPedidos(item);
    }
  };

  const imprimirPicking = (datos, ventana, etiquetaDocumentos, numerosDocumentos, esReporteNotas, idsDocumentos) => {

    setActivate(true);

    const numeros = Array.isArray(numerosDocumentos)
      ? numerosDocumentos
      : records
        .filter((registro) => idseleccionados.indexOf(registro.id) >= 0)
        .map((registro) => registro.numero);

    const ids = Array.isArray(idsDocumentos)
      ? idsDocumentos
      : records
        .filter((registro) => idseleccionados.indexOf(registro.id) >= 0)
        .map((registro) => registro.id);

    imprimirReportePicking(
      datos,
      {
        empresa: afiliado,
        usuario: usuario,
        fechasistema: fechasistema ? moment(fechasistema).format('DD-MM-YYYY') : '',
        titulo: 'Reporte Picking List',
        pedidos: numeros,
        etiquetaDocumentos: etiquetaDocumentos || 'PEDIDOS',
        esNota: esReporteNotas === true,
      },
      ventana
    )
      .then(function () {
        return marcarDespachados(ids, esReporteNotas === true);
      })
      .catch(function (error) {
        cerrarVentana(ventana);
        setMensaje({
          mostrar: true,
          titulo: "Error",
          texto: mensajeError(error),
          icono: "error",
        });
      })
      .finally(() => {
        setActivate(false);
      });
  };

  const imprimirPickingNotas = (seleccionados, ventana) => {
    const almacenes = [];
    const productosInfo = {};

    seleccionados.forEach((documento) => {
      if (
        documento.idalmacen !== null &&
        documento.idalmacen !== undefined &&
        almacenes.indexOf(documento.idalmacen) < 0
      ) {
        almacenes.push(documento.idalmacen);
      }

      documento.filas.forEach((fila) => {
        if (fila.idproducto !== undefined && fila.idproducto !== null && !productosInfo[fila.idproducto]) {
          productosInfo[fila.idproducto] = {
            nombre: fila.nomproducto || '',
            factor: Number(fila.factor) || 0,
          };
        }
      });
    });

    setActivate(true);

    const peticiones = almacenes.map((idalmacen) => axios.get(
      `${op.conexion}/api/inventario/productoexisselectubi`,
      {
        params: { idalmacen: idalmacen },
        headers: {
          "x-access-token": `${token}`,
        },
      }
    ));

    Promise.all(peticiones)
      .then(function (respuestas) {
        const existencias = {};

        respuestas.forEach((respuesta, indice) => {
          const idalmacenConsulta = almacenes[indice];
          const productos = Array.isArray(respuesta.data) ? respuesta.data : [];

          productos.forEach((producto) => {
            const idalmacen = producto.idalmacen !== undefined
              ? producto.idalmacen
              : idalmacenConsulta;
            const clave = idalmacen + '|' + producto.idproducto;
            const actual = existencias[clave] || {
              cantidad: 0,
              factor: Number(producto.valorfactor) || 0,
              descripcionuni: producto.descripcionuni,
              nombre: producto.nombrecorto || '',
            };

            actual.cantidad += Number(producto.cantidad) || 0;
            if (!actual.factor) actual.factor = Number(producto.valorfactor) || 0;
            if (!actual.descripcionuni) actual.descripcionuni = producto.descripcionuni;
            if (!actual.nombre) actual.nombre = producto.nombrecorto || '';
            existencias[clave] = actual;
          });
        });

        const datos = construirPickingNotas(seleccionados, existencias, unidadPorId, productosInfo);

        if (datos.length === 0) {
          cerrarVentana(ventana);
          setMensaje({ mostrar: true, titulo: 'Notificación', texto: '¡ No se encontraron datos para las notas seleccionadas !', icono: 'informacion' });
          setActivate(false);
          return;
        }

        imprimirPicking(
          datos,
          ventana,
          'NOTAS DE ENTREGA',
          seleccionados.map((documento) => documento.numero),
          true,
          seleccionados.map((documento) => documento.id)
        );
      })
      .catch(function (error) {
        cerrarVentana(ventana);
        setMensaje({
          mostrar: true,
          titulo: "Error",
          texto: mensajeError(error),
          icono: "error",
        });
        setActivate(false);
      });
  };

  const selectDatos = () => {

    const seleccionados = records.filter(
      (registro) => idseleccionados.indexOf(registro.id) >= 0
    );

    if (esNota) {
      let ventanaNotas = window.open('', 'ReportePickingNotas', 'fullscreen=no,scrollbars=yes,toolbar=no,resizable=no,width=850,height=700');
      imprimirPickingNotas(seleccionados, ventanaNotas);
      return;
    }

    let ventana = window.open('', 'ReportePicking', 'fullscreen=no,scrollbars=yes,toolbar=no,resizable=no,width=850,height=700');
    const ids = seleccionados.map((registro) => registro.id);
    // fn_reporte_picking recibe el estado de despacho (statusr) igual que la
    // lista, por eso se envia el filtro actual: con statusr=0 fijo la funcion
    // no devuelve los pedidos ya despachados y no se pueden reimprimir.
    const statusReporte = Number(statusdespacho) === 1 ? 1 : 0;
    let endpoint = `${op.conexion}/api/pedido/pedidoPicking?statusr=${statusReporte}&idpedidos=${encodeURIComponent(JSON.stringify(ids))}`;

    setActivate(true);

    axios
      .get(endpoint, {
        headers: {
          "x-access-token": `${token}`,
        },
      })
      .then(function (response) {
        if (response.status === 200) {
          if (response.data.length > 0) {
            return imprimirPicking(
              response.data,
              ventana,
              'PEDIDOS',
              seleccionados.map((registro) => registro.numero),
              false,
              ids
            );
          } else {
            cerrarVentana(ventana);
            setMensaje({ mostrar: true, titulo: 'Notificación', texto: '¡ No se encontraron datos para los pedidos seleccionados !', icono: 'informacion' });
          }
        }
      })
      .catch(function (error) {
        cerrarVentana(ventana);
        setMensaje({
          mostrar: true,
          titulo: "Error",
          texto: mensajeError(error),
          icono: "error",
        });
      })
      .finally(() => {
        setActivate(false);
      });
  };

  const onChangeValidar = (e) => {
    e.preventDefault();

    if (idseleccionados.length === 0) {
      setMensaje({
        mostrar: true,
        titulo: 'Notificación',
        texto: esNota
          ? '¡ Debe seleccionar al menos una nota de entrega !'
          : '¡ Debe seleccionar al menos un pedido !',
        icono: 'informacion'
      });
      return;
    }

    selectDatos();
  };

  const handleCloseSi = () => {
    setMensajesino({ mostrar: false, titulo: '', texto: '', icono: '' });
  };

  const cerrarModal = () => {
    blanquear();
    props.onHideCancelar();
    if (props.onCerrar) props.onCerrar();
  };

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className='azulGradiant2 header-modal'>
        <div className='fw-bold' style={{ color: "#fff" }}><h3>REPORTE PICKING LIST</h3></div>

        <a href='#' ref={btnSalir} onClick={cancelar} className='text-light' ><i className="fas fa-times fa-1x shadow" > {/* Salir */}</i></a>

      </Modal.Header>
      <Modal.Body style={{ backgroundColor: "#FFF" }}>
        <Mensaje mensaje={mensaje}
          onHide={() => {
            mensaje.titulo === 'Exito.' ?
              cerrarModal() :
              setMensaje({ mostrar: false, titulo: '', texto: '', icono: '' })
          }
          } />
        <MensajeSiNo mensaje={mensajesino}
          onHideNo={() => setMensajesino({ mostrar: false, titulo: '', texto: '', icono: '' })} onHideSi={handleCloseSi} />
        <Dimmer active={activate} inverted>
          <Loader inverted>cargando...</Loader>
        </Dimmer>
        <div className='col-md-12 row mx-auto px-0 m-0'>

          <fieldset className="picking-filtros w-100 mx-auto mb-2">
            <legend className="picking-filtros__leyenda float-none w-auto px-1 m-0">
              Filtros del reporte
            </legend>

            <div className="picking-filtros__grid">
              <div className="picking-filtros__campo">
                <label htmlFor="txtDesde" className="form-label">
                  <i className="far fa-calendar-alt" aria-hidden="true"></i>
                  Desde
                </label>
                <input
                  type="date"
                  value={desde}
                  onChange={(e) => cambiarDesde(e.target.value)}
                  className="form-control"
                  id="txtDesde"
                />
              </div>

              <div className="picking-filtros__campo">
                <label htmlFor="txtHasta" className="form-label">
                  <i className="far fa-calendar-check" aria-hidden="true"></i>
                  Hasta
                </label>
                <input
                  type="date"
                  value={hasta}
                  onChange={(e) => cambiarHasta(e.target.value)}
                  className="form-control"
                  id="txtHasta"
                />
              </div>

              <div className="picking-filtros__campo">
                <label htmlFor="txtPor" className="form-label">
                  <i className="far fa-file-alt" aria-hidden="true"></i>
                  Tipo de documento
                </label>
                <select
                  id="txtPor"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  className="form-control form-select"
                >
                  <option value={TIPO_PEDIDO}>Pedidos</option>
                  <option value={TIPO_NOTA}>Notas de entrega</option>
                </select>
              </div>

              <div className="picking-filtros__campo">
                <label htmlFor="txtStatusDespacho" className="form-label">
                  <i className="fas fa-truck" aria-hidden="true"></i>
                  Estado de despacho
                </label>
                <select
                  id="txtStatusDespacho"
                  value={statusdespacho}
                  onChange={(e) => setStatusdespacho(e.target.value)}
                  className="form-control form-select"
                >
                  <option value={STATUS_POR_DESPACHAR}>POR DESPACHAR</option>
                  <option value={STATUS_DESPACHADO}>DESPACHADOS</option>
                </select>
              </div>

              <div className="picking-filtros__campo picking-filtros__campo--vendedor">
                <label htmlFor="txtVendedor" className="form-label">
                  <i className="fas fa-user-tie" aria-hidden="true"></i>
                  Vendedor
                </label>
                <select
                  id="txtVendedor"
                  value={idvendedor}
                  onChange={(e) => setIdvendedor(e.target.value)}
                  className="form-control form-select"
                >
                  <option value={0}>Todos los vendedores</option>
                  {vendedores.map((vendedor) => (
                    <option key={vendedor.idagente} value={vendedor.idagente}>{vendedor.nombre}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="picking-filtros__resumen">
              <span>
                {records.length} documento(s) {Number(statusdespacho) === 1 ? 'despachado(s)' : 'pendiente(s) por despachar'}
              </span>
              <span>
                {esNota ? 'Notas de entrega' : 'Pedidos'} del {desde ? moment(desde).format('DD-MM-YYYY') : '--'} al {hasta ? moment(hasta).format('DD-MM-YYYY') : '--'}
              </span>
            </div>
          </fieldset>

          <TblContainer
            container={{
              height: 300,
              width: "100%",
              overflow: "auto",
              padding: "2px",
              fontSizeAdjust: "0.6",
            }}
          >
            <TblHead />
            <TableBody className="overflow-hidden table-bordered">
              {records.map((item) => (
                <TableRow
                  key={item.id}
                  style={{ padding: "0", fontSizeAdjust: "0.6" }}
                >
                  <TableCell className="bg-light text-center">
                    <div
                      className="form-check d-flex justify-content-center align-items-center m-0"
                      style={{ paddingLeft: 0 }}
                    >
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={idseleccionados.indexOf(item.id) >= 0}
                        onChange={handleCheckDocumento(item.id)}
                        id={"chkdocumento" + item.id}
                        style={{ position: 'relative', margin: 0, float: 'none' }}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="bg-light text-center fw-bold">
                    {item.numero}
                  </TableCell>
                  <TableCell className="align-baseline bg-light" title={item.cliente}>
                    {item.cliente}
                  </TableCell>
                  <TableCell className="align-baseline bg-light" title={nombreVendedor(item.idvendedor)}>
                    {nombreVendedor(item.idvendedor) || '-'}
                  </TableCell>
                  <TableCell className="bg-light text-center">
                    {item.fecha ? moment(item.fecha).format('DD-MM-YYYY') : '-'}
                  </TableCell>
                  <TableCell className="bg-light text-center">
                    {item.filas.length}
                  </TableCell>
                  <TableCell className="bg-light text-center">
                    <a
                      href="#"
                      title={esNota ? "Ver / Imprimir nota de entrega" : "Ver / Imprimir pedido"}
                      className="text-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        selectDocumento(item);
                      }}
                    >
                      <i className={esNota ? "fas fa-file-invoice" : "fas fa-eye"}></i>
                    </a>
                  </TableCell>
                </TableRow>
              ))}
              {records.length === 0 && !activate && (
                <TableRow>
                  <TableCell colSpan={headCells.length} className="text-center text-muted small py-4">
                    No se encontraron documentos para los filtros seleccionados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </TblContainer>
        </div>
      </Modal.Body >
      <Modal.Footer>
        <div className="row m-0 col-md-12 mx-auto">
          <div className='col-md-11 ml-auto px-0 m-0 d-flex justify-content-end align-items-center'>
            <span className='text-muted small' style={{ marginRight: 'auto' }}>
              {idseleccionados.length + ' documento(s) seleccionado(s)'}
            </span>
            <button ref={btnAcepta} onClick={onChangeValidar} disabled={activate} className="btn btn-sm rounded-pill btn-success mx-1"><i className="fas fa-check-circle">  Imprimir</i></button>
            <button ref={btnCancela} onClick={cancelar} className="btn btn-danger btn-sm rounded-pill mx-1"><i className="fas fa-window-close"> Cancelar</i></button>
          </div>
        </div>

      </Modal.Footer>
    </Modal >
  );
}




// WEBPACK FOOTER //
// ./src/components/ventas/modalReportePicking.js